import type { AssetRecord, AssetStore } from './AssetContracts';

const DATABASE = 'rl-web-assets-v1';
const CACHE = 'rl-web-asset-bytes-v1';

/** CacheStorage holds payloads; IndexedDB holds small eviction records only. */
export class BrowserAssetStore implements AssetStore {
  #database: Promise<IDBDatabase> | null = null;

  async withExclusiveLock<T>(operation: () => Promise<T>): Promise<T> {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      // Without cross-tab locking, use verified network bytes instead of unsafe persistence.
      return Promise.reject(new Error('Cross-tab asset storage locking is unavailable.'));
    }
    return await navigator.locks.request(DATABASE, operation);
  }

  async read(key: string): Promise<ArrayBuffer | null> {
    const response = await (await caches.open(CACHE)).match(this.#url(key));
    return response ? response.arrayBuffer() : null;
  }

  async write(record: AssetRecord, bytes: ArrayBuffer): Promise<void> {
    const cache = await caches.open(CACHE);
    // Metadata first means an interrupted write leaves a removable record, not orphan bytes.
    await this.#request('readwrite', store => store.put(record));
    try { await cache.put(this.#url(record.key), new Response(bytes)); }
    catch (error) {
      await this.remove(record.key).catch(() => undefined);
      throw error;
    }
  }

  async remove(key: string): Promise<void> {
    await (await caches.open(CACHE)).delete(this.#url(key));
    await this.#request('readwrite', store => store.delete(key));
  }

  records(): Promise<AssetRecord[]> {
    return this.#request('readonly', store => store.getAll());
  }

  async touch(key: string, at: number): Promise<void> {
    const database = await this.#open();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction('entries', 'readwrite');
      const store = transaction.objectStore('entries');
      const request = store.get(key);
      request.onsuccess = () => {
        const record = request.result as AssetRecord | undefined;
        if (record) store.put({ ...record, lastAccess: at });
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = transaction.onabort = () => reject(transaction.error ?? new Error('Asset metadata update failed.'));
    });
  }

  async estimate(): Promise<{ quota?: number; usage?: number }> {
    return typeof navigator !== 'undefined' && navigator.storage?.estimate ? navigator.storage.estimate() : {};
  }

  #url(key: string): string {
    return new URL(`/__asset_cache__/${encodeURIComponent(key)}`, globalThis.location.origin).href;
  }

  #open(): Promise<IDBDatabase> {
    if (!this.#database) {
      this.#database = new Promise<IDBDatabase>((resolve, reject) => {
        let blocked = false;
        const request = indexedDB.open(DATABASE, 1);
        request.onupgradeneeded = () => request.result.createObjectStore('entries', { keyPath: 'key' });
        request.onsuccess = () => {
          const database = request.result;
          if (blocked) { database.close(); return; }
          database.onversionchange = () => { database.close(); this.#database = null; };
          resolve(database);
        };
        request.onerror = () => reject(request.error);
        request.onblocked = () => { blocked = true; reject(new Error('Asset metadata database is blocked.')); };
      }).catch(error => { this.#database = null; throw error; });
    }
    return this.#database;
  }

  async #request<T>(mode: IDBTransactionMode, requestFor: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const database = await this.#open();
    return new Promise<T>((resolve, reject) => {
      const transaction = database.transaction('entries', mode);
      const request = requestFor(transaction.objectStore('entries'));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = transaction.onabort = () => reject(transaction.error ?? request.error ?? new Error('Asset metadata operation failed.'));
    });
  }
}
